import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { atLeast } from "@/lib/roles";
import { listClosures, createClosure, updateClosure, cancelClosure } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Pencil, X, Bell, BellOff, Eye } from "lucide-react";

const fmt = (iso) => new Date(iso + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
const tomorrow = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const t12 = (x) => {
  if (!x) return "";
  const [h, m] = x.split(":").map(Number);
  return `${h % 12 || 12}${m ? `:${String(m).padStart(2, "0")}` : ""} ${h < 12 ? "am" : "pm"}`;
};
const empty = () => ({ start_date: tomorrow(), end_date: "", kind: "closed", open_from: "07:00", open_to: "11:00", reason: "", message: "", notify: true });

// Mirrors the server's wording (routes/closures.py) so the manager sees what members will get.
function preview(f, gym) {
  if (!f.start_date) return null;
  const end = f.end_date || f.start_date;
  const one = end === f.start_date;
  const a = new Date(f.start_date + "T00:00:00"), b = new Date(end + "T00:00:00");
  const mon = (d) => d.toLocaleDateString("en-GB", { month: "short" });
  const when = one ? `${a.toLocaleDateString("en-GB", { weekday: "short" })} ${a.getDate()} ${mon(a)}`
    : a.getMonth() === b.getMonth() ? `${a.getDate()}–${b.getDate()} ${mon(b)}` : `${a.getDate()} ${mon(a)} – ${b.getDate()} ${mon(b)}`;
  const reason = f.reason.trim() || "Holiday";
  const title = f.kind === "closed" ? `${gym} closed ${when}` : `${gym} timings ${when}`;
  const body = f.kind === "closed"
    ? `${reason}: the gym is closed ${one ? "on " : ""}${when}.`
    : `${reason}: we're open ${t12(f.open_from)} to ${t12(f.open_to)} ${one ? "on " : ""}${when}.`;
  return { title, body: f.message.trim() ? `${body} ${f.message.trim()}` : body };
}

export default function GymClosures() {
  const { activeFacility, effectiveRole, user } = useAuth();
  const canEdit = atLeast(effectiveRole || user?.role, "manager");
  const [data, setData] = useState(null);
  const [past, setPast] = useState(false);
  const [editing, setEditing] = useState(null);   // null | {id?, ...form}
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await listClosures(past));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load closures");
      setData({ closures: [], today: "", members_on_app: 0 });
    }
  }, [past]);
  useEffect(() => { load(); }, [load, activeFacility?.id]);

  const gym = activeFacility?.name || "The gym";
  const pv = useMemo(() => (editing ? preview(editing, gym) : null), [editing, gym]);
  const today = data?.today || "";
  const upcoming = (data?.closures || []).filter((c) => c.end_date >= today);
  const done = (data?.closures || []).filter((c) => c.end_date < today).reverse();

  const save = async () => {
    const f = editing;
    if (f.reason.trim().length < 2) { toast.error("Say why, e.g. Diwali"); return; }
    const body = {
      start_date: f.start_date, end_date: f.end_date || null, kind: f.kind, reason: f.reason.trim(),
      message: f.message.trim() || null, notify: f.notify,
      ...(f.kind === "hours" ? { open_from: f.open_from, open_to: f.open_to } : {}),
    };
    setSaving(true);
    try {
      const r = f.id ? await updateClosure(f.id, body) : await createClosure(body);
      toast.success(r.notified ? `Saved and sent to ${data?.members_on_app ?? 0} members on the app` : "Saved (members not notified)");
      setEditing(null);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(Array.isArray(d) ? d[0]?.msg?.replace("Value error, ", "") : d || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const cancel = async (c) => {
    const notify = window.confirm(`Cancel ${c.reason} (${c.label})?\n\nOK = cancel and tell members the gym is open as usual.\nCancel = keep it.`);
    if (!notify) return;
    try {
      await cancelClosure(c.id, true);
      toast.success("Cancelled; members told the gym is open");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't cancel");
    }
  };

  return (
    <div className="space-y-6" data-testid="gym-closures">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Member App · Gym</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Closures &amp; Holidays</h2>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Enter a closure or changed timings once. Members on the app at {gym} get a notification (they can't switch these off),
            the WOD is hidden on closed days, and closed days don't count towards the weekly no-show follow-up.
          </p>
        </div>
        {canEdit && <Button onClick={() => setEditing(empty())} data-testid="closure-add"><Plus className="mr-1 h-4 w-4" /> Add closure</Button>}
      </div>

      {!canEdit && <div className="flex items-center gap-2 text-xs text-zinc-500"><Eye className="h-3.5 w-3.5" /> View only. Managers add closures.</div>}

      {data === null ? (
        <div className="card-surface p-8 text-center text-sm text-zinc-500">Loading…</div>
      ) : upcoming.length === 0 ? (
        <div className="card-surface p-10 text-center text-sm text-zinc-500">No closures coming up. Add festival holidays as soon as they're decided so members can plan.</div>
      ) : (
        <div className="space-y-2" data-testid="closures-upcoming">
          {upcoming.map((c) => <Row key={c.id} c={c} today={today} canEdit={canEdit} onEdit={() => setEditing({ ...empty(), ...c, end_date: c.end_date === c.start_date ? "" : c.end_date, message: c.message || "", open_from: c.open_from || "07:00", open_to: c.open_to || "11:00", notify: true })} onCancel={() => cancel(c)} />)}
        </div>
      )}

      <div>
        <button type="button" className="text-xs text-zinc-500 hover:text-zinc-300" onClick={() => setPast(!past)}>
          {past ? "Hide" : "Show"} older closures
        </button>
        {done.length > 0 && (
          <div className="mt-2 space-y-2 opacity-70">{done.map((c) => <Row key={c.id} c={c} today={today} />)}</div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit closure" : "Add closure"}</DialogTitle>
            <DialogDescription>For one day leave "Last day" empty.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="flex gap-2">
                {[["closed", "Closed all day"], ["hours", "Changed timings"]].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setEditing({ ...editing, kind: k })}
                    className={`rounded-md border px-3 py-1.5 text-xs ${editing.kind === k ? "border-emerald-500/60 text-zinc-100" : "border-zinc-800 text-zinc-400"}`}>{l}</button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="First day"><Input type="date" value={editing.start_date} onChange={(e) => setEditing({ ...editing, start_date: e.target.value })} /></Field>
                <Field label="Last day (optional)"><Input type="date" value={editing.end_date} min={editing.start_date} onChange={(e) => setEditing({ ...editing, end_date: e.target.value })} /></Field>
              </div>
              {editing.kind === "hours" && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Open from"><Input type="time" value={editing.open_from} onChange={(e) => setEditing({ ...editing, open_from: e.target.value })} /></Field>
                  <Field label="Open until"><Input type="time" value={editing.open_to} onChange={(e) => setEditing({ ...editing, open_to: e.target.value })} /></Field>
                </div>
              )}
              <Field label="Reason"><Input value={editing.reason} onChange={(e) => setEditing({ ...editing, reason: e.target.value })} placeholder="Diwali" data-testid="closure-reason" /></Field>
              <Field label="Extra message (optional)">
                <Textarea rows={2} value={editing.message} onChange={(e) => setEditing({ ...editing, message: e.target.value })} placeholder="Happy Diwali from the LIFTR team! The WOD is posted in the app for home training." />
              </Field>
              <label className="flex items-center gap-2 text-sm text-zinc-300">
                <input type="checkbox" checked={editing.notify} onChange={(e) => setEditing({ ...editing, notify: e.target.checked })} />
                {editing.id ? "Tell members about the change" : "Tell members now"} ({data?.members_on_app ?? 0} on the app)
              </label>
              {editing.notify && pv && (
                <div className="rounded-lg border border-zinc-800 bg-[#0b0b0b] p-3 text-sm" data-testid="closure-preview">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Notification preview</div>
                  <div className="mt-1 font-semibold text-zinc-100">{editing.id ? "Update: " : ""}{pv.title}</div>
                  <div className="text-zinc-300">{pv.body}</div>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={save} disabled={saving} data-testid="closure-save">{saving ? "Saving…" : editing.notify ? "Save and send" : "Save"}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ c, today, canEdit, onEdit, onCancel }) {
  const now = c.start_date <= today && today <= c.end_date;
  return (
    <div className="card-surface flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-testid={`closure-${c.id}`}>
      <div>
        <div className="flex items-center gap-2">
          <span className="font-semibold text-zinc-100">{c.reason}</span>
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${c.kind === "closed" ? "bg-red-500/15 text-red-300" : "bg-amber-500/15 text-amber-300"}`}>
            {c.kind === "closed" ? "Closed" : `Open ${c.hours}`}
          </span>
          {now && <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-300">Today</span>}
        </div>
        <div className="text-sm text-zinc-400">{c.start_date === c.end_date ? fmt(c.start_date) : `${fmt(c.start_date)} – ${fmt(c.end_date)}`}</div>
        <div className="mt-0.5 flex items-center gap-1 text-[11px] text-zinc-500">
          {c.announcement_ids?.length ? <><Bell className="h-3 w-3" /> Members notified</> : <><BellOff className="h-3 w-3" /> Not announced</>}
          {c.created_by_name ? ` · added by ${c.created_by_name}` : ""}
        </div>
      </div>
      {canEdit && (
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" onClick={onEdit}><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
          <Button variant="ghost" size="sm" onClick={onCancel} className="text-red-300"><X className="mr-1 h-3.5 w-3.5" /> Cancel</Button>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-zinc-400">{label}</span>
      {children}
    </label>
  );
}
